import { useCallback, useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Layout } from "providers/Layout";
import { instance } from "api/apiBase";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  BaseInput,
  IconButton,
  IconButtonSize,
  Show
} from "shared/components";
import { InputSize } from "shared/components/forms/DateInput";
import { FilterLines } from "untitledui-js-base";
import { Tabs } from "shared/components/Tabs";
import { Pagination } from "shared/components/Pagination";
import { IdentityTheftTable } from "./components/IdentityTheftTable";
import { IdentityTheftFilterDrawer } from "./components/FilterDrawer";
import { ResendInviteModal } from "./components/ResendInviteModal";
import { getColumns } from "./columns";
import type {
  IdentityMonitoringResponse,
  IdentityTheftProtectionRow,
  IdentityTheftFilters
} from "./types";
import {
  StatusTab,
  TAB_TO_API_STATUS,
  TABS,
  PAGE_SIZE,
  INITIAL_FILTERS,
  transformEnrollment
} from "./utils";

export const IdentityTheftProtectionView = () => {
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<StatusTab>(StatusTab.All);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<IdentityTheftFilters>(INITIAL_FILTERS);
  const [resendTarget, setResendTarget] =
    useState<IdentityTheftProtectionRow | null>(null);

  const apiStatus = TAB_TO_API_STATUS[activeTab];

  const { data, isLoading } = useQuery<IdentityMonitoringResponse>({
    queryKey: [
      "IDENTITY_MONITORING_ENROLLMENTS",
      apiStatus,
      page,
      search,
      filters
    ],
    placeholderData: keepPreviousData,
    queryFn: () =>
      instance
        .get("portal/v1/identity_monitoring_enrollments", {
          params: {
            enrollment_status: apiStatus,
            page,
            per_page: PAGE_SIZE,
            ...(search ? { search } : {}),
            ...(filters.selectedProperties.length > 0
              ? { "property_ids[]": filters.selectedProperties }
              : {}),
            ...(filters.selectedStates.length > 0
              ? { "states[]": filters.selectedStates }
              : {}),
            ...(filters.lastActivityFrom
              ? { last_activity_from: filters.lastActivityFrom }
              : {}),
            ...(filters.lastActivityTo
              ? { last_activity_to: filters.lastActivityTo }
              : {})
          }
        })
        .then(({ data }) => data)
  });

  const rows = useMemo(
    () => (data?.items ?? []).map(transformEnrollment),
    [data?.items]
  );

  const statusCounts = data?.extra.status_counts;
  const totalCount = data?.total_count ?? 0;
  const totalPages = data?.page_count ?? 1;

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.selectedProperties.length > 0) count++;
    if (filters.selectedStates.length > 0) count++;
    if (filters.lastActivityFrom) count++;
    if (filters.lastActivityTo) count++;
    return count;
  }, [filters]);

  const handleTabChange = useCallback((t: string) => {
    setActiveTab(t as StatusTab);
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleApplyFilters = (newFilters: IdentityTheftFilters) => {
    setFilters(newFilters);
    setPage(1);
  };

  const columns = useMemo(() => getColumns((row) => setResendTarget(row)), []);

  return (
    <Layout
      headerClassName="py-0"
      header={
        <div className="px-6 py-6 border-b border-alpha-100">
          <h1 className="text-2xl text-content-1000 font-bold">
            Identity theft protection
          </h1>
        </div>
      }
    >
      <div className="mt-4 text-base text-content-1000">
        <div className="flex flex-col gap-4 mb-4 min-w-0">
          <div className="flex gap-2">
            <BaseInput
              id="itp-search"
              label=""
              placeholder="Search"
              value={search}
              onChange={handleSearchChange}
              className="!mb-0 max-w-[271px] !bg-white"
              size={InputSize.SM}
            />
            <div className="flex items-center gap-2">
              <div className="relative">
                <IconButton
                  Icon={FilterLines}
                  size={IconButtonSize.SM}
                  onClick={() => setShowFilters(true)}
                />
                <Show if={activeFiltersCount > 0}>
                  <Badge
                    variant={BadgeVariant.PRIMARY}
                    size={BadgeSize.SM}
                    className="absolute -right-1.5 -top-1.5 !min-w-[18px] !h-[18px] !px-1 flex items-center justify-center"
                  >
                    {activeFiltersCount}
                  </Badge>
                </Show>
              </div>
            </div>
          </div>
          <div className="w-fit">
            <Tabs
              tabs={TABS}
              selected={activeTab}
              setActiveTab={handleTabChange}
              displayMap={{
                [StatusTab.Enrolled]: (
                  <>
                    Enrolled
                    {(statusCounts?.enrolled ?? 0) > 0 && (
                      <div className="flex items-center justify-center rounded bg-primary-500 text-[10px] text-background-1000 px-2">
                        {statusCounts!.enrolled}
                      </div>
                    )}
                  </>
                ),
                [StatusTab.Invited]: (
                  <>
                    Invited
                    {(statusCounts?.pending_opt_in ?? 0) > 0 && (
                      <div className="flex items-center justify-center rounded bg-danger-500 text-[10px] text-background-1000 px-2">
                        {statusCounts!.pending_opt_in}
                      </div>
                    )}
                  </>
                ),
                [StatusTab.Declined]: (
                  <>
                    Declined
                    {(statusCounts?.canceled ?? 0) > 0 && (
                      <div className="flex items-center justify-center rounded bg-danger-500 text-[10px] text-background-1000 px-2">
                        {statusCounts!.canceled}
                      </div>
                    )}
                  </>
                ),
                [StatusTab.All]: "All"
              }}
              tabClassName="!w-auto"
            />
          </div>
        </div>

        <IdentityTheftTable<IdentityTheftProtectionRow>
          columns={columns}
          rows={rows}
          loading={isLoading && !data}
          footer={
            <div className="flex items-center justify-between min-w-0">
              <p className="text-sm text-content-300 font-medium min-w-0">
                {totalCount} {totalCount === 1 ? "resident" : "residents"}
              </p>
              <Pagination
                className="justify-end shrink-0 !w-fit"
                total={totalPages}
                current={page}
                onChange={setPage}
              />
            </div>
          }
        />
      </div>

      <IdentityTheftFilterDrawer
        open={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        onApply={handleApplyFilters}
      />

      <ResendInviteModal
        target={resendTarget}
        onClose={() => setResendTarget(null)}
      />
    </Layout>
  );
};
